import type { HomeChatPanelProps as Props } from './HomeChatPanel.types';
import { constrainedPurchaseGuidanceFromThread, constrainedWorkflowChoiceFromThread } from './constrainedWorkflowProjection';
import { HomeChatHttpError } from './api';
import { useCallback } from 'react';
import { AlertColor } from '@mui/material/Alert';
import { extractGpcRouterResponse } from './gpcHandoff';
import type { GpcChatSessionView } from '../GpcChat/types';
import type { MrChatSessionView } from '../MrChat/types';
import type { ConstructionSowChatSessionView } from '../ConstructionSowChat/types';
import type { ServicesPwsChatSessionView } from '../ServicesPwsChat/types';
import { getThread, getBoundMrChat, getBoundConstructionSowChat, getBoundServicesPwsChat } from './api';
import { normalizeV2Turn } from '../FarChat/api';
import type { RawV2Turn } from '../FarChat/api';
import type { HomeChatCard } from './types';
import { normalizeHomeChatCards } from './types';
import { ACTION_CHOICE_UNAVAILABLE_COPY } from './homeChatChoiceUtils';
import { makeRequestId, coerceAttachments } from './homeChatSessionUtils';
import { normalizeWorkflowDocumentHistory } from './workflowDocumentHistory';
import type { HomeThreadResponse } from './types';
import type { HomeChatPanelState } from './useHomeChatPanelState';


type Inputs = { preserveControllerCards?: boolean } & Pick<HomeChatPanelState, 'setWorkflowOnlyChoice' | 'setWorkflowOnlyEntryMessage' | 'setWorkflowOnlyPurchaseGuidance'> &
Required<Pick<Props, 'workflowOnly'>> &
Pick<HomeChatPanelState,
  | 'setNotice'
  | 'setChatCards'
  | 'activeSessionIdRef'
  | 'gpcViewsByHomeSessionRef'
  | 'setRehydratingGpcSessionId'
  | 'setActiveSessionId'
  | 'setGpcChatView'
  | 'setMrChatView'
  | 'setPendingGpcRouter'
  | 'setOptimisticGpcOpening'
  | 'setServicesPwsChatView'
  | 'setConstructionSowChatView'
  | 'pendingRequestIdRef'
  | 'setMessages'
  | 'setAttachments'
  | 'setTurns'
  | 'setWorkflowDocumentHistory'
  | 'setBranchRevision'
  | 'setPendingRequestId'
>;

export function useHomeChatHydration({
  preserveControllerCards = false,
  setNotice,
  setChatCards,
  activeSessionIdRef,
  gpcViewsByHomeSessionRef,
  setRehydratingGpcSessionId,
  setActiveSessionId,
  setGpcChatView,
  setMrChatView,
  setPendingGpcRouter,
  setOptimisticGpcOpening,
  setServicesPwsChatView,
  setConstructionSowChatView,
  pendingRequestIdRef,
  setMessages,
  setAttachments,
  setTurns,
  setBranchRevision,
  setWorkflowDocumentHistory,
  setPendingRequestId,
  setWorkflowOnlyChoice,
  setWorkflowOnlyEntryMessage,
  setWorkflowOnlyPurchaseGuidance,
  workflowOnly,
}: Inputs) {

  const hydrateDocumentHistory = useCallback((thread: HomeThreadResponse, sessionId: string) => {
    setWorkflowDocumentHistory(normalizeWorkflowDocumentHistory(thread, sessionId));
  }, [setWorkflowDocumentHistory]);

  const notify = useCallback((severity: AlertColor, message: string) => {
    setNotice({ severity, message });
  }, []);

  const chatOpenErrorMessage = useCallback((error: unknown) => (
    workflowOnly && error instanceof HomeChatHttpError && error.status === 409
      ? 'Chat is not available for this workflow. Please click Open Workflow instead.'
      : error instanceof Error && error.message ? error.message : 'Failed to load chat.'
  ), [workflowOnly]);

  const replaceChatCard = useCallback((nextCard: HomeChatCard) => {
    setChatCards((previous) => {
      const withSupersededChoices = nextCard.kind === 'action_choice'
        ? previous.map((card) => card.kind === 'action_choice' && card.card_id !== nextCard.card_id && card.state === 'pending'
          ? { ...card, state: 'error' as const, options: [], error: ACTION_CHOICE_UNAVAILABLE_COPY }
          : card)
        : previous;
      const index = withSupersededChoices.findIndex((card) => card.card_id === nextCard.card_id);
      if (index === -1) return [...withSupersededChoices, nextCard];
      return withSupersededChoices.map((card) => card.card_id === nextCard.card_id ? nextCard : card);
    });
  }, []);

  /**
   * Adopt only a server-hydrated GPC controller view. The persisted Home Chat
   * binding remains the source of truth across reloads; this state is merely
   * the currently rendered projection. A freshly-created GPC view can also
   * identify the Home Chat session that the backend bound on its behalf.
  */
  const applyGpcChatView = useCallback((view: GpcChatSessionView) => {
    const homeSessionId = view.home_chat_session_id || activeSessionIdRef.current;
    if (homeSessionId) {
      gpcViewsByHomeSessionRef.current.set(homeSessionId, view);
      setRehydratingGpcSessionId((current) => current === homeSessionId ? null : current);
    }
    if (homeSessionId && homeSessionId !== activeSessionIdRef.current) {
      activeSessionIdRef.current = homeSessionId;
      setActiveSessionId(homeSessionId);
    }
    setMrChatView(null);
    setConstructionSowChatView(null);
    setServicesPwsChatView(null);
    setGpcChatView(view);
  }, []);

  const applyMrChatView = useCallback((view: MrChatSessionView) => {
    const homeSessionId = view.home_chat_session_id || activeSessionIdRef.current;
    if (homeSessionId && homeSessionId !== activeSessionIdRef.current) {
      activeSessionIdRef.current = homeSessionId;
      setActiveSessionId(homeSessionId);
    }
    setGpcChatView(null);
    setConstructionSowChatView(null);
    setServicesPwsChatView(null);
    setMrChatView(view);
  }, []);

  const applyConstructionSowChatView = useCallback((view: ConstructionSowChatSessionView) => {
    const homeSessionId = view.home_chat_session_id || activeSessionIdRef.current;
    if (homeSessionId && homeSessionId !== activeSessionIdRef.current) {
      activeSessionIdRef.current = homeSessionId;
      setActiveSessionId(homeSessionId);
    }
    setMrChatView(null);
    setGpcChatView(null);
    setPendingGpcRouter(null);
    setOptimisticGpcOpening(null);
    setRehydratingGpcSessionId(null);
    setChatCards([]);
    setWorkflowOnlyPurchaseGuidance(null);
    setServicesPwsChatView(null);
    setConstructionSowChatView(view);
  }, []);

  const applyServicesPwsChatView = useCallback((view: ServicesPwsChatSessionView) => {
    const homeSessionId = view.home_chat_session_id || activeSessionIdRef.current;
    if (homeSessionId && homeSessionId !== activeSessionIdRef.current) {
      activeSessionIdRef.current = homeSessionId;
      setActiveSessionId(homeSessionId);
    }
    setMrChatView(null);
    setGpcChatView(null);
    setConstructionSowChatView(null);
    setServicesPwsChatView(null);
    setPendingGpcRouter(null);
    setOptimisticGpcOpening(null);
    setRehydratingGpcSessionId(null);
    setChatCards([]);
    setServicesPwsChatView(view);
  }, []);

  /**
   * A durable MR binding takes precedence over transcript cards. Older backend
   * thread hydration may carry only `mr_binding`, so resolve the authoritative
   * controller view through its owner-bound start/resume endpoint rather than
   * letting a previously claimed intent selector remain interactive.
   */
  const hydrateMrBinding = useCallback(async (
    thread: {
      mr_chat?: MrChatSessionView | null;
      mr_binding?: { mr_chat_session_id?: string } | null;
    },
    homeSessionId = activeSessionIdRef.current || '',
    isCurrent: () => boolean = () => true,
  ) => {
    if (!isCurrent()) return false;
    if (thread.mr_chat?.session_id) {
      applyMrChatView(thread.mr_chat);
      return true;
    }
    if (thread.mr_binding?.mr_chat_session_id) {
      const view = await getBoundMrChat(homeSessionId);
      if (!isCurrent()) return false;
      applyMrChatView(view);
      return true;
    }
    return false;
  }, [applyMrChatView]);

  /** Restore either the bound GPC controller or its pending confirmation. */
  const hydrateGpcState = useCallback((thread: {
    gpc_chat?: GpcChatSessionView | null;
    gpc_pending_confirmation?: unknown;
  }, blockedByBoundController: boolean) => {
    if (!blockedByBoundController && thread.gpc_chat?.session_id) {
      applyGpcChatView(thread.gpc_chat);
      setPendingGpcRouter(null);
      return;
    }
    setGpcChatView(null);
    const pending = blockedByBoundController
      ? null
      : extractGpcRouterResponse(thread.gpc_pending_confirmation);
    setPendingGpcRouter(
      pending?.kind === 'start_confirmation'
        || pending?.kind === 'ambiguous_choice'
        || pending?.kind === 'resume_selection'
        ? pending
        : null
    );
  }, [applyGpcChatView]);

  /** Restore a construction-SOW controller only for the selected Home Chat. */
  const hydrateConstructionSowBinding = useCallback(async (
    thread: {
      construction_sow_chat?: ConstructionSowChatSessionView | null;
      construction_sow_binding?: {
        construction_sow_chat_session_id?: string;
      } | null;
    },
    homeSessionId = activeSessionIdRef.current || '',
    isCurrent: () => boolean = () => true,
  ) => {
    if (!isCurrent()) return false;
    if (thread.construction_sow_chat?.session_id) {
      if (thread.construction_sow_chat.home_chat_session_id && thread.construction_sow_chat.home_chat_session_id !== homeSessionId) return false;
      applyConstructionSowChatView(thread.construction_sow_chat);
      return true;
    }
    if (thread.construction_sow_binding?.construction_sow_chat_session_id) {
      const view = await getBoundConstructionSowChat(
        homeSessionId,
        makeRequestId(),
      );
      if (!isCurrent()) return false;
      if (view.home_chat_session_id && view.home_chat_session_id !== homeSessionId) return false;
      applyConstructionSowChatView(view);
      return true;
    }
    return false;
  }, [applyConstructionSowChatView]);

  /** Restore a Services PWS controller only from its server-owned binding. */
  const hydrateServicesPwsBinding = useCallback(async (
    thread: {
      services_pws_chat?: ServicesPwsChatSessionView | null;
      services_pws_chat_binding?: { services_pws_chat_session_id?: string } | null;
    },
    homeSessionId = activeSessionIdRef.current || '',
    isCurrent: () => boolean = () => true,
  ) => {
    if (!isCurrent()) return false;
    if (thread.services_pws_chat?.session_id) {
      if (thread.services_pws_chat.home_chat_session_id && thread.services_pws_chat.home_chat_session_id !== homeSessionId) return false;
      applyServicesPwsChatView(thread.services_pws_chat);
      return true;
    }
    if (thread.services_pws_chat_binding?.services_pws_chat_session_id) {
      const view = await getBoundServicesPwsChat(homeSessionId, makeRequestId());
      if (!isCurrent()) return false;
      if (view.home_chat_session_id && view.home_chat_session_id !== homeSessionId) return false;
      applyServicesPwsChatView(view);
      return true;
    }
    return false;
  }, [applyServicesPwsChatView]);

  /**
   * Stale-resolve guard. An in-flight lazy-create/turn fetch captures the
   * `crid` it dispatched with (held in `pendingRequestId`). If the user
   * switches/creates/deletes a session before the fetch resolves, the
   * navigation handlers clear `pendingRequestId`, so the captured `crid`
   * no longer matches the live one and the late-resolving handler must
   * NOT apply its `setPendingClarification` / `setActiveSessionId` /
   * `setTurns` (which would clobber the pane the user navigated to).
   * Returns true when the dispatch is stale (caller should no-op).
   */
  const isStaleResolve = useCallback((crid?: string): boolean => {
    if (!crid) return false;
    return pendingRequestIdRef.current !== crid;
  }, []);

  /**
   * Direct similar-requirements responses intentionally contain only one
   * safe result card. The submitted user utterance lives in the durable
   * thread timeline, so hydrate after a card response instead of treating
   * that one card as the complete local transcript. This keeps successive
   * searches visible in chronological order and avoids replacing earlier
   * cards with a later zero-result response.
   */
  const hydrateDirectSearchThread = useCallback(async (
    sessionId: string,
    isCurrent: () => boolean,
  ): Promise<boolean> => {
    try {
      const thread = await getThread(sessionId);
      if (!isCurrent()) return false;
      hydrateDocumentHistory(thread, sessionId);
      setMessages(thread.messages || []);
      setAttachments(coerceAttachments(thread.attachments));
      const rawTurns = (thread.turns || []) as RawV2Turn[];
      setTurns(rawTurns.map((turn) => normalizeV2Turn(turn)));
      setBranchRevision(
        Number.isSafeInteger((thread as { branch_revision?: unknown }).branch_revision)
          ? (thread as { branch_revision: number }).branch_revision
          : 0,
      );
      const hasMrBinding = await hydrateMrBinding(
        thread,
        sessionId,
        isCurrent,
      );
      if (!isCurrent()) return false;
      const hasServicesPwsBinding = !hasMrBinding
        && await hydrateServicesPwsBinding(thread, sessionId, isCurrent);
      if (!isCurrent()) return false;
      const hasConstructionSowBinding = !hasMrBinding && !hasServicesPwsBinding
        && await hydrateConstructionSowBinding(
          thread,
          sessionId,
          isCurrent,
        );
      if (!isCurrent()) return false;
      const hasBoundController = hasMrBinding || hasServicesPwsBinding || hasConstructionSowBinding;
      const constrainedChoice = workflowOnly
        ? constrainedWorkflowChoiceFromThread(thread)
        : null;
      const constrainedGuidance = workflowOnly ? constrainedPurchaseGuidanceFromThread(thread) : null;
      if (workflowOnly && thread.constrained_session) {
        setWorkflowOnlyChoice(constrainedChoice?.card || null);
        setWorkflowOnlyPurchaseGuidance(constrainedGuidance?.card || null);
        if (constrainedChoice?.entryMessage || constrainedGuidance?.entryMessage) {
          setWorkflowOnlyEntryMessage(constrainedChoice?.entryMessage || constrainedGuidance?.entryMessage || null);
        }
      }
      setChatCards(
        (hasBoundController && !preserveControllerCards && !hasMrBinding) || (workflowOnly && !hasMrBinding && !constrainedGuidance && !constrainedChoice)
          ? [] : normalizeHomeChatCards(thread.chat_cards),
      );
      hydrateGpcState(thread, hasBoundController);
      return true;
    } catch {
      // The POST response card remains a safe, usable fallback if the
      // follow-up hydration races with a transient network failure.
      return false;
    }
  }, [hydrateConstructionSowBinding, hydrateGpcState, hydrateMrBinding, hydrateServicesPwsBinding, workflowOnly, preserveControllerCards, hydrateDocumentHistory]);

  /**
   * Finally-block teardown of a send's in-flight signal. Clears
   * `pendingRequestId` (and its mirror ref) ONLY when the ref still points
   * at THIS turn's `crid`. If the user navigated away (ref cleared) and
   * possibly started a new in-flight turn (ref now holds the new crid),
   * this no-ops so it can't clobber the newer turn's live `pendingRequestId`.
   */
  const clearPendingIfOwned = useCallback((crid?: string) => {
    if (crid && pendingRequestIdRef.current !== crid) {
      return; // a newer turn (or navigation) owns the slot now
    }
    pendingRequestIdRef.current = null;
    setPendingRequestId(null);
  }, []);

  return {
    chatOpenErrorMessage,

    notify,
    hydrateDocumentHistory,
    replaceChatCard,
    applyGpcChatView,
    applyMrChatView,
    applyConstructionSowChatView,
    applyServicesPwsChatView,
    hydrateMrBinding,
    hydrateGpcState,
    hydrateConstructionSowBinding,
    hydrateServicesPwsBinding,
    isStaleResolve,
    hydrateDirectSearchThread,
    clearPendingIfOwned,
  };
}

export type HomeChatHydration = ReturnType<typeof useHomeChatHydration>;
